import { CSSProperties, useCallback } from 'react';
import { ANYONE_ID } from 'Calendar/anyone';
import shopSelectors from 'services/selectors/shop';
import { css } from 'styled-components';
import { readableTextOn } from 'utils/color-contrast';

/** A staff's group color and the text color that stays readable on it. */
export type StaffGroupTone = { background: string; color: string };

/**
 * Resolves the Staff Group color of a staff, so every staff card and list paints the same color the
 * day-view calendar header does and the front desk can spot a staff by color alone.
 *
 * Returns a lookup rather than taking a staff id, so lists can call it inside `.map`. Anyone and
 * ungrouped staff resolve to undefined and keep their normal look.
 */
export const useStaffGroupTone = () => {
  const byStaffId = shopSelectors.staffGroupsByStaffId();
  return useCallback((staffId?: string | number | null): StaffGroupTone | undefined => {
    if (staffId === undefined || staffId === null) return undefined;
    const id = staffId.toString();
    if (id === ANYONE_ID) return undefined;
    const colorHex = byStaffId[id]?.colorHex;
    return colorHex ? { background: colorHex, color: readableTextOn(colorHex) } : undefined;
  }, [byStaffId]);
};

/** Fills a staff card or row with the group color. */
export const toneFill = (tone?: StaffGroupTone): CSSProperties | undefined =>
  tone ? { background: tone.background, color: tone.color } : undefined;

/**
 * Avatar on a group-filled card: a white border lifts it off the fill, and a letter avatar (no photo)
 * gets a white letter, which a dark ink letter on the translucent avatar disc would not be.
 */
export const toneAvatar = (tone?: StaffGroupTone): CSSProperties | undefined =>
  tone ? { border: '2px solid #FFFFFF', color: '#FFFFFF' } : undefined;

/** Ring around an avatar, for compact rows (dropdown options, picked-staff chips) too small to fill. */
export const toneRing = (tone?: StaffGroupTone): CSSProperties | undefined =>
  tone ? { boxShadow: `0 0 0 3px ${tone.background}` } : undefined;

/** Pill behind a staff name, paired with `toneRing` on compact rows. */
export const toneChip = (tone?: StaffGroupTone): CSSProperties | undefined =>
  tone ? { background: tone.background, color: tone.color, borderRadius: 999, padding: '2px 8px' } : undefined;

/** Recolors a single-color icon image (e.g. a blue SVG) to the tone's readable text color. */
export const toneIconFilter = (tone?: StaffGroupTone): CSSProperties | undefined =>
  tone ? { filter: tone.color === readableTextOn('#FFFFFF') ? 'brightness(0)' : 'brightness(0) invert(1)' } : undefined;

/**
 * Border for a group-filled card: the fill replaces the card's own "selected" background, so the
 * selection has to show as a thick border in the screen's accent color instead.
 */
export const groupedSelectionBorder = (selected: boolean, accent: string): CSSProperties =>
  selected ? { border: `4px solid ${accent}` } : {};

/**
 * Repaints the read-only antd Input that shows the picked staff (filter bars). Uses !important
 * because those inputs already force their text color with !important.
 */
export const tonedPickedInput = (tone?: StaffGroupTone) => tone ? css`
  .ant-input-affix-wrapper { background: ${tone.background} !important; }
  input { color: ${tone.color} !important; }
` : '';
